import React from "react";
import { IdCode, IIdCode } from "source/models/common/idCode";
import { CheckBox } from "ui/Scripts/source/components/framework/checkbox";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { CalendarTaskRepetitionType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { SelectIdCodeDialog } from "../framework/selectIdCodeDialog";
import { EndlessList, IEndlessList } from "ui/Scripts/source/models/common/endlessList";
import { Radio } from "ui/Scripts/source/components/framework/radio";
import moment from "moment";
import { DatePicker } from "../framework/datePicker";

/* eslint-disable no-unused-vars */
enum CalendarTaskRepetitionEndType {
    Count = "0",
    Date = "1"
}
/* eslint-enable no-unused-vars */

export interface IRepetitionIntervalYear {
    day: number;
    month: number;
}

interface CalendarTaskRepetitionSettingsProps {
    isReadOnly: boolean;
    orgStartDate: number;
    startDateStr: string;
    repetitionSettingsVisible: boolean;
    repetitionIntervalType: CalendarTaskRepetitionType;
    repetitionInterval: number;
    repetitionIntervalWeek: number[];
    repetitionIntervalMonth: number;
    repetitionIntervalYear: IRepetitionIntervalYear;
    repetitionEndRepeatCount: number;
    repetitionEndDateStr: string;
    onChange: (name: string, value: string | number | IRepetitionIntervalYear | boolean | number[]) => void;
}

interface CalendarTaskRepetitionSettingsState {
    showSelectRepetitionTypeDialog: boolean;
    showSelectMonthDialog: boolean;
    repetitionTypes: IdCode[];
    months: IdCode[];
}

export const CalendarTaskRepetitionSettings = (props: CalendarTaskRepetitionSettingsProps) => {
    const [state, setState] = React.useState<CalendarTaskRepetitionSettingsState>({
        showSelectRepetitionTypeDialog: false,
        showSelectMonthDialog: false,
        repetitionTypes: [
            IdCode.createIdCode(CalendarTaskRepetitionType.NotRepeated.toString(10), Translations.NotRepeated),
            IdCode.createIdCode(CalendarTaskRepetitionType.Weekly.toString(10), Translations.Weekly),
            IdCode.createIdCode(CalendarTaskRepetitionType.Monthly.toString(10), Translations.Monthly),
            IdCode.createIdCode(CalendarTaskRepetitionType.Yearly.toString(10), Translations.Yearly),
        ],
        months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) =>
            IdCode.createIdCode(month.toString(10), Base.monthNumberToName(month, appConfig.culture) + (appConfig.culture === "fi" ? "ta" : ""))
        ),
    });
    
    const getDefaultRepetitionIntervalCount = (repetitionIntervalType: CalendarTaskRepetitionType) => {
        let result = 0;
        if (repetitionIntervalType === CalendarTaskRepetitionType.Weekly) {
            result = 10;
        } else if (repetitionIntervalType === CalendarTaskRepetitionType.Monthly) {
            result = 12;
        } else {
            result = 2;
        }
        return result;
    };

    const getDefaultRepetitionIntervalWeek = (): number[] => {
        const orgStartDate = props.orgStartDate ? new Date(props.orgStartDate) : new Date();
        return [((props.startDateStr ? props.startDateStr.toDate(orgStartDate) : orgStartDate).getDay() + 6) % 7];
    };

    const getDefaultRepetitionIntervaMonth = (): number => {
        const orgStartDate = props.orgStartDate ? new Date(props.orgStartDate) : new Date();
        return (props.startDateStr ? props.startDateStr.toDate(orgStartDate) : orgStartDate).getDate();
    };

    const getDefaultRepetitionIntervalYear = (): IRepetitionIntervalYear => {
        const orgStartDate = props.orgStartDate ? new Date(props.orgStartDate) : new Date();
        const date = (props.startDateStr ? props.startDateStr.toDate(orgStartDate) : orgStartDate);
        return { month: date.getMonth() + 1, day: date.getDate() };
    };

    const handleChangeRepetitionTypeDialogOk = (id: string, _code: string) => {
        const type = parseInt(id, 10);
        const repetitionEnabledFirstTime = props.repetitionInterval < 1 && type > CalendarTaskRepetitionType.NotRepeated;
        const weekRepetitionEnabled = props.repetitionIntervalType !== CalendarTaskRepetitionType.Weekly && type === CalendarTaskRepetitionType.Weekly;
        const monthRepetitionEnabled = props.repetitionIntervalType !== CalendarTaskRepetitionType.Monthly && type === CalendarTaskRepetitionType.Monthly;
        const yearRepetitionEnabled = props.repetitionIntervalType !== CalendarTaskRepetitionType.Yearly && type === CalendarTaskRepetitionType.Yearly;
        setState((old) => ({ ...old, showSelectRepetitionTypeDialog: false }));
        props.onChange("repetitionIntervalType", type);
        if (repetitionEnabledFirstTime) {
            props.onChange("repetitionInterval", 1);
            props.onChange("repetitionEndRepeatCount", getDefaultRepetitionIntervalCount(type));
        }
        if (weekRepetitionEnabled) {
            props.onChange("repetitionIntervalWeek", getDefaultRepetitionIntervalWeek());
        } else if (monthRepetitionEnabled) {
            props.onChange("repetitionIntervalMonth", getDefaultRepetitionIntervaMonth());
        } else if (yearRepetitionEnabled) {
            props.onChange("repetitionIntervalYear", getDefaultRepetitionIntervalYear());
        }
    };

    const handleChangeRepetitionTypeDialogCancel = () => {
        setState((old) => ({ ...old, showSelectRepetitionTypeDialog: false }));
    };

    const handleRepetitionIntervalChange = (event) => {
        const value: string = event.target.value;
        props.onChange("repetitionInterval", Math.min(value.toInteger(false, 1), 99));
    };

    const handleRepetitionEndChange = (value: string) => {
        if (value === "0") {
            props.onChange("repetitionEndRepeatCount", getDefaultRepetitionIntervalCount(props.repetitionIntervalType));
            props.onChange("repetitionEndDateStr", "");
        } else {
            const repetitionEndDate = moment();
            if (props.repetitionIntervalType === CalendarTaskRepetitionType.Weekly) {
                repetitionEndDate.add(2, "months");
            } else if (props.repetitionIntervalType === CalendarTaskRepetitionType.Monthly) {
                repetitionEndDate.add(12, "months");
            } else {
                repetitionEndDate.add(2, "years");
            }
            props.onChange("repetitionEndRepeatCount", 0);
            props.onChange("repetitionEndDateStr", Base.utcTimeToDateStr(parseInt(repetitionEndDate.format("x"))));
        }
    };

    const handleRepetitionEndRepeatCountChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        props.onChange("repetitionEndRepeatCount", Math.min(value.toInteger(false, 1), 99));
    };

    const handleRepetitionEndDateChange = (value: string) => {
        const minDate = Base.getNowUtcDate();
        const maxDate = minDate.addYears(10);
        let repetitionEndDate = value.toUtcDate(minDate);
        if (repetitionEndDate.getTime() < minDate.getTime()) {
            repetitionEndDate = minDate;
        } else if (repetitionEndDate.getTime() > maxDate.getTime()) {
            repetitionEndDate = maxDate;
        }
        props.onChange("repetitionEndDateStr", Base.utcTimeToDateStr(repetitionEndDate.getTime()));
    };

    const handleRepetitionYearDayChange = (event) => {
        const value: string = event.target.value;
        const parts = Base.getValidDateParts(Math.min(value.toInteger(false, 1), 31), props.repetitionIntervalYear.month, null);
        const result: IRepetitionIntervalYear = {
            day: parts.day,
            month: parts.month
        };
        props.onChange("repetitionIntervalYear", result);
    };

    const handleRepetitionYearMonthChange = (id: string, _code: string) => {
        const value: number = parseInt(id);
        const parts = Base.getValidDateParts(props.repetitionIntervalYear.day, Math.min(value, 12), null);
        setState((old) => ({ ...old, showSelectMonthDialog: false }));
        const result: IRepetitionIntervalYear = {
            day: parts.day,
            month: parts.month
        };
        props.onChange("repetitionIntervalYear", result);
    };

    const handleRepetitionMonthDayChange = (event) => {
        const value: string = event.target.value;
        props.onChange("repetitionIntervalMonth", Math.min(value.toInteger(false, 1), 31));
    };

    const handleRepetitionWeekDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const day = event.target.name.toInteger();
        const result = props.repetitionIntervalWeek.filter(i => i.toString(10) !== day.toString(10));
        if (event.target.checked) {
            result.push(day);
        }
        if (!result.length) return;
        props.onChange("repetitionIntervalWeek", result);
    };
    
    const handleLoadRepetitionTypeTypes = (page: number): Promise<IEndlessList<IIdCode>> => {
        return new Promise<IEndlessList<IIdCode>>((resolve, reject) => {
            const result = new EndlessList<IIdCode>(null, IdCode);
            result.items = state.repetitionTypes;
            result.page = 1;
            result.hasMore = false;
            resolve(result);
        });
    };

    const handleLoadMonths = (page: number): Promise<IEndlessList<IIdCode>> => {
        return new Promise<IEndlessList<IIdCode>>((resolve, reject) => {
            const result = new EndlessList<IIdCode>(null, IdCode);
            result.items = state.months;
            result.page = 1;
            result.hasMore = false;
            resolve(result);
        });
    };

    const repetitionEndType = props.repetitionEndRepeatCount > 0 ? CalendarTaskRepetitionEndType.Count : CalendarTaskRepetitionEndType.Date;

    return (
        <>
            <div className="sectionTitle">
                {Translations.Recurrence}
                <div
                    className={"btn " + (props.repetitionSettingsVisible ? "collapseUp" : "expandDown")}
                    onClick={() => props.onChange("repetitionSettingsVisible", !props.repetitionSettingsVisible)}
                >
                    <span className="icon"></span>
                </div>
            </div>
            {props.repetitionSettingsVisible && (
                <>
                    <div className="form-group">
                        <div>
                            <label className="control-label">{Translations.TaskRepetitionType}</label>
                            <ToolButton
                                tooltip={Translations.Change}
                                classes={"edit round mono smaller right"}
                                disabled={props.isReadOnly}
                                onClick={() => setState((old) => ({ ...old, showSelectRepetitionTypeDialog: true }))}
                            />
                        </div>
                        <div>
                            {state.repetitionTypes.find(item => parseInt(item.id) === props.repetitionIntervalType)?.code}
                        </div>
                    </div>
                    {props.repetitionIntervalType !== CalendarTaskRepetitionType.NotRepeated && (
                        <>
                            <div className="row">
                                <div className="col-12">
                                    <div
                                        style={{ display: "flex", alignItems: "center", marginTop: "1rem" }}
                                    >
                                        <span style={{ width: "100px" }}>{Translations.CalendarTaskRepetitionRepeatIntervalPrefix}</span>
                                        <input
                                            className="form-control"
                                            title={Translations.Week}
                                            type="number"
                                            disabled={props.isReadOnly}
                                            value={props.repetitionInterval}
                                            onChange={handleRepetitionIntervalChange}
                                            style={{ margin: "0 1rem", width: "50px" }}
                                        />
                                        <span>{props.repetitionIntervalType === CalendarTaskRepetitionType.Weekly
                                            ? Translations.CalendarTaskRepetitionRepeatIntervalWeeklySuffix
                                            : (props.repetitionIntervalType === CalendarTaskRepetitionType.Monthly
                                                ? Translations.CalendarTaskRepetitionRepeatIntervalMonthlySuffix
                                                : Translations.CalendarTaskRepetitionRepeatIntervalYearlySuffix)}</span>
                                    </div>
                                </div>
                            </div>
                            {props.repetitionIntervalType === CalendarTaskRepetitionType.Weekly && (
                                <div style={{ marginBottom: "1rem" }}>
                                    <div className="row">
                                        {[0, 1, 2, 3, 4, 5, 6].map((day) =>
                                            <div key={day} className="col-6" style={{ marginTop: "1rem" }}>
                                                <CheckBox
                                                    title={Base.dayNumberToName(day + 1, appConfig.culture.replace("-US", ""))}
                                                    name={day.toString(10)}
                                                    checked={props.repetitionIntervalWeek.findIndex(i => i.toString(10) === day.toString(10)) > -1}
                                                    disabled={props.isReadOnly}
                                                    onClick={handleRepetitionWeekDayChange}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {props.repetitionIntervalType === CalendarTaskRepetitionType.Monthly &&
                                <div className="row">
                                    <div className="col-12">
                                        <div
                                            style={{ display: "flex", alignItems: "center", marginBottom: "1rem", marginTop: "1rem" }}
                                        >
                                            <div style={{ width: "100px" }}>{Translations.CalendarTaskRepetitionRepeatIntervalMonthPrefix}</div>
                                            <input
                                                className="form-control"
                                                type="number"
                                                disabled={props.isReadOnly}
                                                value={props.repetitionIntervalMonth}
                                                style={{ margin: "0 1rem", width: "50px" }}
                                                onChange={handleRepetitionMonthDayChange}
                                            />
                                            <div>{Translations.CalendarTaskRepetitionRepeatIntervalMonthSuffix}</div>
                                        </div>
                                    </div>
                                </div>
                            }
                            {props.repetitionIntervalType === CalendarTaskRepetitionType.Yearly &&
                                <div className="row">
                                    <div className="col-12">
                                        <div
                                            style={{ display: "flex", alignItems: "center", marginBottom: "1rem", marginTop: "1rem" }}
                                        >
                                            <div style={{ width: "100px" }}></div>
                                            <input
                                                className="form-control"
                                                title={Translations.Day}
                                                type="number"
                                                disabled={props.isReadOnly}
                                                style={{ margin: "0 1rem", width: "50px" }}
                                                value={props.repetitionIntervalYear.day}
                                                onChange={handleRepetitionYearDayChange}
                                            />
                                            {!!Translations.CalendarTaskRepetitionRepeatIntervalDayMonthSeparator &&
                                                <span>{Translations.CalendarTaskRepetitionRepeatIntervalDayMonthSeparator}&nbsp;</span>
                                            }
                                            <div>
                                                {Base.monthNumberToName(props.repetitionIntervalYear.month, appConfig.culture) + (appConfig.culture === "fi" ? "ta" : "")}
                                            </div>
                                            <div style={{ marginLeft: "auto" }}>
                                                <ToolButton
                                                    tooltip={Translations.Change}
                                                    classes={"edit round mono smaller right"}
                                                    disabled={props.isReadOnly}
                                                    onClick={() => setState((old) => ({ ...old, showSelectMonthDialog: true }))}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                            <div className="row">
                                <div className="col-12">
                                    <div className="form-group">
                                        <div style={{ marginTop: "1rem", marginBottom: ".5rem" }}>{Translations.CalendarTaskRepetitionEnd}</div>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
                                            <Radio
                                                title=""
                                                checked={props.repetitionEndRepeatCount > 0}
                                                disabled={props.isReadOnly}
                                                onClickBoolean={(_checked: boolean) => { handleRepetitionEndChange("0"); }}
                                            />
                                            {!!Translations.CalendarTaskRepetitionCountPrefix &&
                                                <div style={{ width: "105px" }}>{Translations.CalendarTaskRepetitionCountPrefix}</div>
                                            }
                                            <input
                                                className="form-control"
                                                type="number"
                                                disabled={repetitionEndType === CalendarTaskRepetitionEndType.Date || props.isReadOnly}
                                                maxLength={2}
                                                min={1}
                                                max={99}
                                                value={props.repetitionEndRepeatCount}
                                                style={{ width: "115px", marginLeft: "10px" }}
                                                onChange={handleRepetitionEndRepeatCountChange}
                                            />
                                            {!!Translations.CalendarTaskRepetitionCountSuffix &&
                                                <span style={{ marginLeft: "10px" }}>{Translations.CalendarTaskRepetitionCountSuffix}</span>
                                            }
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <Radio
                                                title=""
                                                checked={props.repetitionEndRepeatCount === 0}
                                                onClickBoolean={(checked: boolean) => { handleRepetitionEndChange("1"); }}
                                            />
                                            {!!Translations.CalendarTaskRepetitionEndDatePrefix &&
                                                <div style={{ width: "105px" }}>{Translations.CalendarTaskRepetitionEndDatePrefix}</div>
                                            }
                                            <div style={{ width: "115px", marginLeft: "10px" }}>
                                                {(repetitionEndType === CalendarTaskRepetitionEndType.Count || props.isReadOnly
                                                    ? <input
                                                        className="form-control disabled"
                                                        readOnly={true}
                                                        disabled={true}
                                                        value={props.repetitionEndDateStr}
                                                    />
                                                    : <DatePicker
                                                        value={props.repetitionEndDateStr}
                                                        openRight={true}
                                                        required={true}
                                                        onChange={handleRepetitionEndDateChange}
                                                        onBlur={handleRepetitionEndDateChange}
                                                    />
                                                )}
                                            </div>
                                            {!!Translations.CalendarTaskRepetitionEndDateSuffix &&
                                                <span style={{ marginLeft: "10px" }}>{Translations.CalendarTaskRepetitionEndDateSuffix}</span>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
            {state.showSelectRepetitionTypeDialog &&
                <SelectIdCodeDialog
                    title={Translations.SelectTaskRepetitionType}
                    selectedId={props.repetitionIntervalType.toString(10)}
                    loadIdCodes={handleLoadRepetitionTypeTypes}
                    onOk={handleChangeRepetitionTypeDialogOk}
                    onCancel={handleChangeRepetitionTypeDialogCancel}
                />
            }
            {state.showSelectMonthDialog && (
                <SelectIdCodeDialog
                    title={Translations.SelectMonth}
                    selectedId={props.repetitionIntervalYear.month.toString(10)}
                    loadIdCodes={handleLoadMonths}
                    onOk={handleRepetitionYearMonthChange}
                    onCancel={() => setState((old) => ({ ...old, showSelectMonthDialog: false }))}
                />
            )}
        </>
    );
};
